import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, Tooltip } from 'antd';
import { Search, User, ChevronDown, Sparkles, LogOut, Settings, Shield, Pencil, Palette } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import './Header.css';

const Header = ({ currentPage, setCurrentPage, setSearchQuery, onLoginClick }) => {
  const [showSearch, setShowSearch] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const { user, isAuthenticated, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isAdmin = isAuthenticated && user && (user.role === 'org_admin' || user.role === 'platform_admin');
  const userMenuRef = useRef(null);
  const isSketchbook = theme === 'sketchbook';

  // 点击外部关闭用户菜单
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await fetch('/api/subjects');
        if (!response.ok) return;
        const data = await response.json();
        setSubjects(data || []);
      } catch (error) {
        // ignore errors for nav list
      }
    };
    fetchSubjects();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  return (
    <header className="header">
      <nav className="container nav-container">
        <div className="logo">
          <img src="/images/logo.png" alt="PING Logo" className="main-logo" />
          <div className="brand-divider"></div>
          <div className="brand-info">
            <span className="brand-title">
              Personalized Instruction and<br />Need-aware Gamification
            </span>
          </div>
        </div>
        
        <ul className="nav-links">
          <li className="dropdown">
            <Link to="/" className={currentPage === 'simulations' ? 'active' : ''}>
              MODULES <ChevronDown size={16} />
            </Link>
            <div className="dropdown-content">
              <Link to="/?subject=all">All Sims</Link>
              <hr />
              {subjects.map((subject) => (
                <Link key={subject.key} to={`/?subject=${subject.key}`}>{subject.name}</Link>
              ))}
            </div>
          </li>
          <li>
            <Link to="/studio" className="nav-studio">
              STUDIO <Sparkles size={16} className="icon-spark" />
            </Link>
          </li>
          <li><Link to="/teaching">TEACHING</Link></li>
          {isAdmin && (
            <li><Link to="/dashboard">DASHBOARD</Link></li>
          )}
          <li><Link to="/research">RESEARCH</Link></li>
          <li className="dropdown">
            <Link to="/initiatives">
              INITIATIVES <ChevronDown size={16} />
            </Link>
          </li>
        </ul>

        <div className="nav-actions">
          <Tooltip title={isSketchbook ? 'Switch to Classic theme' : 'Switch to Sketchbook theme'}>
            <Button
              type="text"
              className={`icon-btn theme-toggle${isSketchbook ? ' is-sketchbook' : ''}`}
              onClick={toggleTheme}
              aria-label="Toggle color theme"
              icon={isSketchbook ? <Palette size={20} /> : <Pencil size={20} />}
            />
          </Tooltip>

          <Button
            type="text"
            className="icon-btn"
            onClick={() => setShowSearch(!showSearch)}
            icon={<Search size={20} />}
          >
          </Button>
          
          {isAuthenticated ? (
            <div className="user-menu-wrapper" ref={userMenuRef}>
              <Button
                type="text"
                className="user-btn"
                onClick={() => setShowUserMenu(!showUserMenu)}
                icon={<User size={20} />}
              >
                <span className="user-name">{user?.full_name || user?.email || 'Login'}</span>
              </Button>
              
              {showUserMenu && (
                <div className="user-dropdown">
                  <div className="user-dropdown-header">
                    <p className="user-email">{user?.email || 'Not logged in'}</p>
                    {user && <p className="user-role">{user?.role}</p>}
                  </div>
                  <hr />
                  {user ? (
                    <>
                      <Link to="/account" onClick={() => setShowUserMenu(false)}>
                        <Settings size={16} />
                        Account Center
                      </Link>
                      {isAdmin && (
                        <Link to="/admin" onClick={() => setShowUserMenu(false)}>
                          <Shield size={16} />
                          Admin Center
                        </Link>
                      )}
                      <Button type="text" onClick={() => { logout(); setShowUserMenu(false); }}>
                        <LogOut size={16} />
                        Sign Out
                      </Button>
                    </>
                  ) : (
                    <Button type="text" onClick={() => { setShowUserMenu(false); onLoginClick(); }}>
                      <User size={16} />
                      Login / Sign Up
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <Button type="text" className="user-actions" icon={<User size={20} />} onClick={onLoginClick} />
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header;
